import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  withWidth,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CountrySearch from "../../Components/CountrySearch";
import { getToken, requestErrorHandler } from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";

function NewContactDialog({ onCreated, width }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("contact");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const isCompany = type === "company";
  const nameField = isCompany ? "companyName" : "contactName";

  const close = () => {
    if (submitting.current) return;
    setOpen(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current || !values[nameField]?.trim()) return;
    submitting.current = true;
    setSaving(true);
    setError("");
    const additionalInfo = {
      contactEmail: (values.contactEmail || "").trim(),
      contactPhone: (values.contactPhone || "").trim(),
    };
    try {
      const response = await fetch(EnvSettings.server + "/edit_contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          contactData: {
            [nameField]: values[nameField].trim(),
            ...(isCompany
              ? { location: values.location, additionalInfo }
              // The existing API uses this spelling for new contact details.
              : { contactAdditonalInfo: additionalInfo }),
          },
        }),
      });
      if (!response.ok) throw new Error("Request failed");
      const result = await response.json();
      requestErrorHandler(result, dispatch);
      if (result.error) {
        setError(t("contacts.saveError"));
        return;
      }
      dispatch(showPopupSnackbar(result));
      setOpen(false);
      onCreated();
    } catch (error) {
      setError(t("contacts.saveError"));
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => {
          setType("contact");
          setValues({});
          setError("");
          setOpen(true);
        }}
      >
        {t("contacts.create")}
      </Button>
      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="sm"
        fullScreen={width === "xs"}
        aria-labelledby="new-contact-title"
      >
        <form onSubmit={submit}>
          <DialogTitle id="new-contact-title">
            {t(isCompany ? "contacts.newCompany" : "contacts.newContact")}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="new-contact-type"
                  select
                  fullWidth
                  variant="outlined"
                  label={t("contacts.type")}
                  value={type}
                  disabled={saving}
                  onChange={(event) => {
                    setType(event.target.value);
                    setError("");
                  }}
                >
                  <MenuItem value="contact">{t("contact")}</MenuItem>
                  <MenuItem value="company">{t("companyList.company")}</MenuItem>
                </TextField>
              </Grid>
              {[nameField, "contactEmail", "contactPhone"].map((field) => (
                <Grid item xs={12} key={field}>
                  <TextField
                    id={"new-" + field}
                    fullWidth
                    required={field === nameField}
                    variant="outlined"
                    label={t("contactInfo." + field)}
                    type={
                      field === "contactEmail"
                        ? "email"
                        : field === "contactPhone"
                        ? "tel"
                        : "text"
                    }
                    inputProps={{ maxLength: 255 }}
                    value={values[field] || ""}
                    disabled={saving}
                    onChange={(event) =>
                      setValues({ ...values, [field]: event.target.value })
                    }
                  />
                </Grid>
              ))}
              {isCompany && (
                <Grid item xs={12}>
                  <CountrySearch
                    disabled={saving}
                    defaultValue={values.location}
                    onChange={(event, value) =>
                      setValues({ ...values, location: value?.country })
                    }
                  />
                </Grid>
              )}
              {error && (
                <Grid item xs={12}>
                  <Typography color="error" role="alert">
                    {error}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={close} disabled={saving}>
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || !values[nameField]?.trim()}
            >
              {t("create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default withWidth()(NewContactDialog);
